import React, { createContext, useState, useMemo, useEffect, useRef} from "react";

// hooks and utils
import { getStartAndEndDate } from  "@/helpers/Dates";
import useDebounce from "@/components/hooks/useDebounce";
import usePollForUpdates from "@/components/hooks/activityfeed/usePollForUpdates";
import useBuildQueryString from "@/components/hooks/utility/useQueryString";
import { getCompositeId, getFeedFilters } from  "@/helpers/ActivityFeed";
import { openInNewTab } from  "@/helpers/http";

//api
import { getActivityFeed, getAppsForActivityFeed, commentLike, postCommentReply, commentDelete, commentUnlike, postActivityFeedOpenedEvent, contentLike, postComment, archiveContent, contentUnlike, approveModeratedContent } from  "@/api/activityFeed";
import { getMyUserProfile } from  "@/api/profile";

//interfaces
import { BannerNotification, IActivityFeedFilterParams } from  "@/interfaces/activityFeed";



export interface IActivityFeedContext {
    appGuid: string,
    isLoading: boolean,
    isMultipleAppOrganisation: boolean,
    noActivitiesMessage: string,
    activities: any[],
    filterDates: Date[],
    apps: { id: number; name: string }[];
    selectedWorkspaces: number[],
    userProfileImage: string,
    bannerNotifications: BannerNotification[],
    selectedFiltersArePulledFromStorage: boolean,
    newActivitiesCount: number,
    totalActivities: number,
    selectedFilters:  string[],
    newNotificationIds: string[],
    showPopOutFilterScreen: boolean,
    onPopOutClick: () => void,
    setIsPopOut: (isPopOut: boolean) => void,
    isDownloadingCSV: boolean,
    setIsDownloadingCSV: (isDownloading: boolean) => void,
    setSelectedWorkspaces: (appIds: number[]) => void,
    getNotification: (uniqueId: string) => void,
    setSelectedFilters: (activityFilter: string[]) => void,
    setFilterDates: (dates: Date[]) => void,
    setIsLoading: (isLoading: boolean) => void,
    setAppGuid: (appGuid: string) => void,
    getActivities: (params: IActivityFeedFilterParams, paginate: boolean) => void,
    getMoreActivities: () => void,
    postCommentOnContent: (activityKey: string, contentId: number, commentString: string, appId:number) => void,
    replyToComment: (activityKey: string, contentId: number, commentId: number, commentString: string, appId:number) => void,
    updateFeed: () => void,
    likeComment: (activityid: string, appId: number, contentId: number, commentId: number, unlike: boolean) => void,
    likeContent: (activityid: string, appId: number, contentId: number, unlike: boolean) => void,
    getOpenOnWebLink: (contentId: number, commentId?: number) => string,
    deleteComment: (activityId: string, appId: number, contentId: number, commentId: number) => void,
    onArchiveContentClick: (activityId: string, parentListId: number, contentId: number) => void,
    setNewNotificationIds: (newIds: string[]) => void,
    onApproveContentClick: (activityId: string, contentId: number) => void,
    downloadAcknowledgementReport: (appGuid: string, contentId: number) => void,
    downloadCSV: () => void,
    downloadFormResponseCSV: (listId: number, contentId: number) => void,
}

export const activityFeedDefaults:IActivityFeedContext = {
    appGuid: "",
    apps:[],
    isLoading: true,
    isMultipleAppOrganisation: false,
    noActivitiesMessage: "No activity to display",
    activities: [],
    filterDates: [],
    selectedWorkspaces: [],
    selectedFilters: [],
    bannerNotifications: [],
    userProfileImage: "",
    selectedFiltersArePulledFromStorage: false,
    newActivitiesCount: 0,
    totalActivities: 0,
    newNotificationIds: [],
    showPopOutFilterScreen: false,
    onPopOutClick: () => {},
    setIsPopOut: () => {},
    isDownloadingCSV: false,
    setIsDownloadingCSV: () => {},
    getNotification: () => '',
    getOpenOnWebLink: () => '',
    deleteComment: () => {},
    setSelectedFilters: () => {}, 
    setSelectedWorkspaces: () => {}, 
    setFilterDates: () => {},
    setIsLoading:() => {},
    setAppGuid:() => {},
    getActivities: () => {},
    postCommentOnContent: () => {},
    replyToComment: () => {},
    likeComment: () => {},
    likeContent: () => {},
    updateFeed: () => {},
    getMoreActivities: () => {},
    onArchiveContentClick: () => {},
    setNewNotificationIds: () => {},
    onApproveContentClick: () => {},
    downloadAcknowledgementReport: () => { },
    downloadCSV: () => {},
    downloadFormResponseCSV: () => {}
}

export const ActivityFeedContext = createContext(activityFeedDefaults);

const ActivityFeedProvider = ({children}:{children:React.ReactNode}) => {
    const [appGuid, setAppGuid] = useState<string>('');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isMultipleAppOrganisation, setIsMultipleAppOrganisation] = useState<boolean>(false);
    const [activities, setActivities] = useState<any>([]);
    const [filterDates, setFilterDates] = useState<Date[]>([]);
    const [apps, setApps] = useState<{id: number, name: string}[]>([]);
    const [selectedWorkspaces, setSelectedWorkspaces] = useState<number[]>([]);
    const [selectedFilters, setSelectedFilters] = useState<string[]>([]);
    const [bannerNotifications, setBannerNotifications] = useState<BannerNotification[]>([]);
    const [selectedFiltersArePulledFromStorage, setSelectedFiltersArePulledFromStorage] = useState<boolean>(false)
    const [totalActivities, setTotalActivities] = useState<number>(0);
    const [userProfileImage, setUserProfileImage] = useState<string>("");
    const [isPopOut, setIsPopOut] = useState<boolean>(false);
    const [isDownloadingCSV, setIsDownloadingCSV] = useState<boolean>(false);

    const [noActivitiesMessage, setNoActivitiesMessage] = useState<string>("No activity to display")
    const {newActivitiesCount, updateLastModified} = usePollForUpdates({filterDates, selectedWorkspaces, selectedFilters, filtersLoaded: selectedFiltersArePulledFromStorage, isPopOut})
    const debouncedFilters = useDebounce({selectedFilters, selectedWorkspaces, filterDates}, 1000)
    const lastQueryParamsRef = useRef<string>('')

    const [newNotificationIds, setNewNotificationIds] = useState<string[]>([]);

    useEffect(() => {
        if( window.location.pathname.toLowerCase().includes('activityfeed')) {
            setUp();
        }   
    }, []);

    useEffect(() => {
        const savedPreferences = window?.localStorage?.getItem('activityfeedprefernces')

        if (savedPreferences) {
            const filters = JSON.parse(savedPreferences)

            if(filters) {
                setSelectedFilters(filters.selectedFilters)
                setSelectedWorkspaces(filters.selectedWorkspaces)
                setFilterDates(filters.filterDates.map((d: string) => new Date(d)))
            }
        }

        setSelectedFiltersArePulledFromStorage(true)
    },[])

    useEffect(() => {
        if(selectedFiltersArePulledFromStorage && window.location.pathname.toLowerCase().includes('activityfeed') || isPopOut) {
            getAppInformation();
        }
    }, [selectedFiltersArePulledFromStorage]);

    useEffect(() => {
        // only do this on the activity feed page or popout feed, context exists on all pages
        if( window.location.pathname.toLowerCase().includes('activityfeed') || isPopOut) {
            window.localStorage.setItem('activityfeedprefernces', JSON.stringify({
                selectedFilters,
                selectedWorkspaces,
                filterDates
            }))
        }
    },[selectedFilters, selectedWorkspaces, filterDates]);

    useEffect(() => {
        if(selectedFiltersArePulledFromStorage && window.location.pathname.toLowerCase().includes('activityfeed') || isPopOut) {
            getNewFeed()
        }
    },[
        JSON.stringify(debouncedFilters.selectedFilters.toSorted()),
        JSON.stringify(debouncedFilters.selectedWorkspaces.toSorted()),
        JSON.stringify(debouncedFilters.filterDates),
        selectedFiltersArePulledFromStorage
    ])


    useEffect(() => {
        if (bannerNotifications.length > 0) {
            const timer = setTimeout(() => { setBannerNotifications([]) }, 3000);
            return () => clearTimeout(timer);
        }
    }, [bannerNotifications]);

    function onPopOutClick() {
        setIsPopOut(true);
        setUp(true);
        getNewFeed();
    }

    function setUp(isPopOut?: boolean){
        getAppInformation();
        getProfileInfo();
        postActivityFeedOpenedEvent(isPopOut ? 'popout' : 'full')
    }

    function getNotification(uniqueId: string) {
        const notification = bannerNotifications.find(n => n.activityId === uniqueId);

        if (notification) {
            return { isSuccess: notification.isSuccess, message: notification.message };
        }
    }

    function getParamsFromFilters(): IActivityFeedFilterParams {
        const [startDate, endDate] = getStartAndEndDate(filterDates)
        
        return {
            workspaceIds: selectedWorkspaces,
            filter: getFeedFilters(selectedFilters),
            startDate: startDate ?? undefined,
            endDate:  endDate ?? undefined,
            top: 20,
            skip: 0
        }
    }

    function getActivitiesOffset() {
        if(newActivitiesCount > totalActivities) {
            return newActivitiesCount - totalActivities
        }
        return 0;
    }

    function updateNewItemsList(newActivities: any) {
        const topActivity=activities[0] 
        const topId = getCompositeId(topActivity.displayName, topActivity.author?.id, topActivity.createdAt)
        let newActivies: string[] = [];
        let idFound: boolean = false;

        for (const a of newActivities) {
            let id = getCompositeId(a.activityType, a.author?.id, a.createdAt)
            if (!idFound) {
                if(topId === id) {
                    idFound = true;
                }
                else {
                    newActivies.push(id)
                }
            }
        }

        setNewNotificationIds(newActivies);

    }

    function clearNewItemsArray(){
        setNewNotificationIds([])
    }

    function getActivities(params: IActivityFeedFilterParams, paginate: boolean, isRefresh?: boolean) {
        getActivityFeed(params, {"Cache-Control": "no-cache"})
        .then((data) =>   {
            setActivities(paginate ? [...activities ,...data.records] : data.records)
            // if refreshing the current view we will want to subtract any offset new activities as well as the new activity that triggered the refresh
            // from the total
            if(!paginate) {
                setTotalActivities(data.total);
            }

            if(isRefresh) {
                updateNewItemsList(data.records)
            }
            else {
                clearNewItemsArray();
            }
            setNoActivitiesMessage("No activity to display");
        })
        .catch(() => {
            (isRefresh || !paginate) && setActivities([]);
            setNoActivitiesMessage("Something went wrong, please refresh and try again.");
        })
        .finally(() => {
            setIsLoading(false);
        });
    }

    function getProfileInfo() {
        getMyUserProfile()
        .then((data) => {
            setUserProfileImage(data.profilePictureUrl);
        })
    }
    
    function getNewFeed() {
        //do stuff when filter params change
        const params = getParamsFromFilters();
        if(lastQueryParamsRef.current === JSON.stringify(params)) {
            return;
        }
        lastQueryParamsRef.current = JSON.stringify(params)
        setIsLoading(true);
        getActivities(params, false)
        updateLastModified(new Date());
    }

    function refreshFeed(){
        // do stuff when we need to refresh the current list and not get new records
        const params = getParamsFromFilters();
        
        getActivities({
            ...params,
            top: activities.length + getActivitiesOffset() + 1,
            skip: 0
        }, false, true)
    }

    function getMoreActivities(){
        //get older activities
        const params = getParamsFromFilters();
        getActivities({
            ...params,
            top: 20,
            skip: activities.length + getActivitiesOffset()
        }, true)
    }

    function updateFeed(){
        //get latest activities
        const params = getParamsFromFilters();
        getActivities({
            ...params,
            top: activities.length + getActivitiesOffset(),
            skip: 0
        }, false)

        updateLastModified(new Date());
    }

    function getAppInformation() {
        function mapAppInformationToApps(records: any): typeof apps {
            return records.map(({appId, name}: {appId: number, name: string}) => ({id: appId, name}))
        }

       // get top 20 apps, should be fine for most people
        getAppsForActivityFeed(20)
        .then(({records, total}: {records:any, total:number}) => {
            // for crazy orgs like tab get ALL the apps
            if(total > 20) {
                getAppsForActivityFeed(total).then(({records}: {records:any}) => {
                    setApps(mapAppInformationToApps(records))
                })
            } else {
                setApps(mapAppInformationToApps(records))
            }
            setIsMultipleAppOrganisation(total > 1);
        });
    }

    function likeComment(activityId: string, appId: number, contentId: number, commentId: number, unlike: boolean) {
        if (unlike) {
            commentUnlike(appId, contentId, commentId)
            .then(() =>   {
                refreshFeed();
            })
            .catch(() => {
                setBannerNotifications(prev => [...prev.filter(n => n.activityId !== activityId), { activityId: activityId, isSuccess: false, message: "Error unliking comment, please try again" }]);
            });
        } else {
            commentLike(appId, contentId, commentId)
            .then(() =>   {
                refreshFeed();
            })
            .catch(() => {
                setBannerNotifications(prev => [...prev.filter(n => n.activityId !== activityId), { activityId: activityId, isSuccess: false, message: "Error liking comment, please try again" }]);
            });
        }
    }

    function deleteComment(activityId: string, appId: number, contentId: number, commentId: number) {
        commentDelete(appId, contentId, commentId)
        .then(() =>   {
            refreshFeed();
        })
        .catch(() => {
            setBannerNotifications(prev => [...prev.filter(n => n.activityId !== activityId), { activityId: activityId, isSuccess: false, message: "Error deleting comment, please try again" }]);
        });
    }

    function replyToComment(activityId: string, contentId: number, commentId: number, commentString: string, appId: number) {
        const payload = { text: commentString }

        postCommentReply(appId, contentId, commentId, payload)
        .then(() =>   {
            setBannerNotifications(prev => [...prev.filter(n => n.activityId !== activityId), { activityId: activityId, isSuccess: true, message: "Reply Added" }]);
        })
        .catch(() => {
            setBannerNotifications(prev => [...prev.filter(n => n.activityId !== activityId), { activityId: activityId, isSuccess: false, message: "Error posting comment, please try again" }]);
        })
        .finally(() => {
            refreshFeed();
        });
    }

    function postCommentOnContent(activityId: string, contentId: number, commentString: string, appId: number) {
        const payload = { text: commentString }

        postComment(appId, contentId,  payload)
        .then(() =>   {
            setBannerNotifications(prev => [...prev.filter(n => n.activityId !== activityId), { activityId: activityId, isSuccess: true, message: "Comment Added" }]);
        })
        .catch(() => {
            setBannerNotifications(prev => [...prev.filter(n => n.activityId !== activityId), { activityId: activityId, isSuccess: false, message: "Error posting comment, please try again" }]);
        })
        .finally(() => {
            refreshFeed();
        });
    }

    function likeContent(activityId: string, appId: number, contentId: number, unlike?: boolean) {
        if (unlike) {
            contentUnlike(appId, contentId)
            .then(() =>   {
                refreshFeed();
            })
            .catch(() => {
                setBannerNotifications(prev => [...prev.filter(n => n.activityId !== activityId), { activityId: activityId, isSuccess: false, message: "Error unliking content, please try again" }]);
            });
        } else {
            contentLike(appId, contentId)
            .then(() =>   {
                refreshFeed();
            })
            .catch(() => {
                setBannerNotifications(prev => [...prev.filter(n => n.activityId !== activityId), { activityId: activityId, isSuccess: false, message: "Error liking content, please try again" }]);
            });
        }
    }

    function onArchiveContentClick(activityId: string, parentListId: number, contentId: number) {
        archiveContent(parentListId, contentId)
        .then(() =>   {
            refreshFeed();
        })
        .catch(() => {
            setBannerNotifications(prev => [...prev.filter(n => n.activityId !== activityId), { activityId: activityId, isSuccess: false, message: "Error archiving content, please try again" }]);
        });
    }

    function getOpenOnWebLink(contentId: number, commentId?: number):string {
        if (commentId) {
            return `${window.location.origin}/api/webaction/open/content/${contentId}/comment/${commentId}`
        }
        return `${window.location.origin}/api/webaction/open/content/${contentId}`
    }

    function downloadCSV() {
        const { top, skip, ...reportParams } = getParamsFromFilters();
        const queryString = useBuildQueryString(reportParams);
        const url = `/api/activityfeed/notificationhistoryreport${queryString && `?${queryString}`}`;

        openInNewTab(url);
        setIsDownloadingCSV(false);
    }

    function downloadFormResponseCSV(contentId: number, formId: number) {
        const url = `/api/content/list/${contentId}/form/${formId}/report`;
        openInNewTab(url);
    }
    
    function onApproveContentClick(activityId: string, contentId: number) {
        approveModeratedContent(contentId)
        .then(() =>   {
            refreshFeed();
        })
        .catch(() => {
            setBannerNotifications(prev => [...prev.filter(n => n.activityId !== activityId), { activityId: activityId, isSuccess: false, message: "Error approving moderated content, please try again" }]);
        });
    }
    
    function downloadAcknowledgementReport(appGuid: string, contentId: number) {
        const url = `a/${appGuid}/Content/DownloadAcknowledgementCsvReport?contentId=${contentId}`;
        openInNewTab(url);
    }
    
    const context = useMemo(() => ({
        appGuid,
        isMultipleAppOrganisation,
        selectedWorkspaces,
        selectedFilters,
        activities,
        isLoading,
        filterDates,
        userProfileImage,
        bannerNotifications,
        apps,
        selectedFiltersArePulledFromStorage,
        newActivitiesCount,
        totalActivities,
        noActivitiesMessage,
        newNotificationIds,
        setAppGuid,
        setSelectedWorkspaces,
        setSelectedFilters,
        setActivities,
        setIsLoading,
        getActivities,
        setFilterDates,
        replyToComment,
        postCommentOnContent,
        likeComment,
        likeContent,
        updateFeed,
        getOpenOnWebLink,
        deleteComment,
        getMoreActivities,
        getNotification,
        onArchiveContentClick,
        setNewNotificationIds,
        setIsPopOut,
        onPopOutClick,
        showPopOutFilterScreen: selectedFiltersArePulledFromStorage,
        onApproveContentClick,
        downloadAcknowledgementReport,
        downloadCSV,
        downloadFormResponseCSV,
        isDownloadingCSV,
        setIsDownloadingCSV
    }), [
        isPopOut,
        isDownloadingCSV,
        activities,
        filterDates,
        apps,
        selectedWorkspaces,
        selectedFilters,
        bannerNotifications,
        selectedFiltersArePulledFromStorage,
        newActivitiesCount,
        totalActivities,
        isLoading,
        isMultipleAppOrganisation,
        newNotificationIds
    ])

    return (
      <ActivityFeedContext.Provider value={context}>
        {children}
      </ActivityFeedContext.Provider>
    );
};

export default ActivityFeedProvider;
